import { AppThunk } from '../../../../../store/types';
import { PlayerController } from '../../../../../effects/player/manager/playerController';
import { Navigator } from '../../../../../effects/navigator';
import { AudioEffects } from '../../../../../effects/audioEffects';
import { getAudioById, getConferenceUserGroups } from '../../../../../store/models/selectors';
import { updateAudioAction } from '../../../../../store/models/actions';
import { TAudioType } from '../../../../../store/models/types';
import { MyStuffUtils } from '../../../../../../common/utils/myStuffUtils';
import { RecordingsEffects } from '../../../../../effects/recordingsEffects';
import { EAudioPageTab } from '../../../AudioPage/components/types';
import { BookEffects } from '../../../../../effects/bookEffects';
import { TConferenceProcessSteps } from '../../../../../../common/rest/conference/conferenceRest';
import { getGeneralStorage } from '../../../../../../common/utils/local-storage/local-storage-helpers';

export class MyLibRecordingsEvents {

	public static onShowNativeChange(showNative: boolean): AppThunk {
		return () => {
			getGeneralStorage().updateData({ recordingsShowNative: showNative });
		}
	}

	public static onClick(
		groupId: number,
		audioId: number,
	): AppThunk {
		return (
			dispatch,
			getState
		) => {
			// an unprocessed call opens processing, not the player, whichever group it sits in
			const audio = getAudioById(getState(), audioId);
			if (audio?.type === TAudioType.CONFERENCE) {
				RecordingsEffects.processConference(audio.audioS3FilePath || '', audioId);
				return;
			}
			if (!groupId) return;

			PlayerController.getInstance().resetMode();
			Navigator.openAudio(groupId, audioId);
		}
	}

	public static onDelete(
		groupId: number,
		audioId: number,
	): AppThunk {
		return async (
			dispatch,
			getState
		) => {
			const audio = getAudioById(getState(), audioId);
			// the tile's loader while the request runs; the group that really holds it, not the list's language group
			const holder = () => getConferenceUserGroups(getState()).find(g => g.audios?.some(a => a.id === audioId));
			const group = holder();
			if (group) dispatch(updateAudioAction(audioId, group.id, { deletingMedia: true }));
			try {
				// Unprocessed conference delete through the conference endpoint
				if (audio?.type === TAudioType.CONFERENCE) {
					return await RecordingsEffects.deleteConference(
						RecordingsEffects.getRoomIdForAudio(audio),
						audioId,
					);
				}
				return await AudioEffects.remove(audioId, groupId);
			} finally {
				// both effects swallow errors: a recording still in its group means the delete failed
				const left = holder();
				if (left) dispatch(updateAudioAction(audioId, left.id, { deletingMedia: false }));
			}
		}
	}

	// Manual "Delete audio"/"Delete video" — call recordings only (backend rejects anything else).
	// Unlike onDelete, this patches the recording in place rather than removing it from the group.
	public static onDeleteAudio(
		groupId: number,
		audioId: number,
	): AppThunk {
		return async () => AudioEffects.deleteMedia(audioId, groupId, 'audio');
	}

	public static onDeleteVideo(
		groupId: number,
		audioId: number,
	): AppThunk {
		return async () => AudioEffects.deleteMedia(audioId, groupId, 'video');
	}

	// Same as choosing saveLevel=transcription in the settings panel — one request, ladder-consistent,
	// instead of two separate deleteMedia calls with no shared status between them.
	public static onDeleteVideoAndAudio(
		groupId: number,
		audioId: number,
	): AppThunk {
		return async (
			dispatch,
			getState
		) => {
			const audio = getAudioById(getState(), audioId);
			return RecordingsEffects.setConferenceSettings(
				RecordingsEffects.getRoomIdForAudio(audio),
				{ saveLevel: 'transcription' },
				audioId,
			);
		}
	}

	// "Delete for everyone": same saveLevel=nothing path the settings panel uses, which wipes the
	// recording (media + raw sources + the library entry) for every participant, not just this one.
	public static onDeleteForEveryone(
		audioId: number,
	): AppThunk {
		return async (
			dispatch,
			getState
		) => {
			const audio = getAudioById(getState(), audioId);
			return RecordingsEffects.setConferenceSettings(
				RecordingsEffects.getRoomIdForAudio(audio),
				{ saveLevel: 'nothing' },
				audioId,
			);
		}
	}

	public static onDownload(
		audioId: number,
	): AppThunk {
		return async () => {
			await MyStuffUtils.downloadAudio(audioId);
		}
	}

	public static onDownloadCaptions(
		audioId: number,
	): AppThunk {
		return async () => {
			await MyStuffUtils.downloadCaptions(audioId);
		}
	}

	public static onDownloadCaptionsWithTimecodes(
		audioId: number,
	): AppThunk {
		return async () => {
			await MyStuffUtils.downloadCaptionsWithTimecodes(audioId);
		}
	}

	public static onCreateBook(
		audioId: number,
	): AppThunk {
		return async () => BookEffects.createBook(audioId);
	}

	public static onOpenBook(
		audioId: number,
	): AppThunk {
		return () => BookEffects.openBook(audioId);
	}

	public static onOpenExercises(
		audioId: number,
	): AppThunk {
		return () => RecordingsEffects.openAudioTab(audioId, EAudioPageTab.EXERCISES);
	}

	public static onReprocess(
		audioId: number,
		force?: boolean,
		steps?: TConferenceProcessSteps,
	): AppThunk {
		return async (
			dispatch,
			getState
		) => {
			const audio = getAudioById(getState(), audioId);
			return RecordingsEffects.reprocessConference(
				RecordingsEffects.getRoomIdForAudio(audio),
				audioId,
				force,
				steps,
			);
		}
	}

	public static onProcess(
		audioId: number,
	): AppThunk {
		return async (
			dispatch,
			getState
		) => {
			const audio = getAudioById(getState(), audioId);
			if (audio?.audioS3FilePath) {
				return RecordingsEffects.processConference(audio.audioS3FilePath, audioId);
			}
			return Promise.reject('no audio');
		}
	}

	public static onCancel(
		audioId: number,
	): AppThunk {
		return async (
			dispatch,
			getState
		) => {
			const audio = getAudioById(getState(), audioId);
			return RecordingsEffects.cancelConference(
				RecordingsEffects.getRoomIdForAudio(audio),
				audioId,
			);
		}
	}

	public static onCloseSettings(
		audioId: number,
	): AppThunk {
		return () => {
			RecordingsEffects.flushConferenceSettings(audioId);
		}
	}

}
